import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';
import { ChevronDown, useClickOutside } from 'qampo-ui-library';
import { InputField } from './InputField';
import { MODAL_ESCAPE_LAYER_ATTRIBUTE } from '../../constants/modalConstants';
import styles from './ColorSwatchPicker.module.css';

export interface ColorSwatchOption<T extends string> {
  id: T;
  // CSS colour painted on the swatch and on the trigger dot.
  color: string;
  // Already-translated accessible name for the swatch.
  label: string;
}

interface ColorSwatchPickerProps<T extends string> {
  label: string;
  options: ColorSwatchOption<T>[];
  selectedId: T;
  onSelect: (id: T) => void;
  className?: string;
}

// Marks the open panel so Modal's Escape handler yields this press to it.
const escapeLayerProps = { [MODAL_ESCAPE_LAYER_ATTRIBUTE]: 'true' };

// Swatch dropdown shared by the colour choices in the app (treatment-plan
// templates, locations). The trigger is a real button; in the panel, arrows
// move between swatches, Home and End jump to the ends, Enter or Space picks
// the focused colour, and Escape closes just the panel — focus returns to the
// trigger whenever the panel closes.
export const ColorSwatchPicker = <T extends string>({
  label,
  options,
  selectedId,
  onSelect,
  className
}: ColorSwatchPickerProps<T>): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const swatchRefs = useRef<(HTMLButtonElement | null)[]>([]);
  useClickOutside(containerRef, () => setIsOpen(false));

  const selected = options.find((option) => option.id === selectedId);
  const lastIndex = options.length - 1;

  // Opening focuses the current colour, so arrow keys start from where the user
  // already is rather than from the top of the palette.
  const handleOpen = () => {
    const currentIndex = options.findIndex((option) => option.id === selectedId);
    setActiveIndex(currentIndex === -1 ? 0 : currentIndex);
    setIsOpen(true);
  };

  useEffect(() => {
    if (isOpen) swatchRefs.current[activeIndex]?.focus();
  }, [isOpen, activeIndex]);

  const closePanel = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleSelect = (id: T) => {
    onSelect(id);
    closePanel();
  };

  const handleSwatchKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Escape') {
      // The open panel claims Escape (see escapeLayerProps), so this closes
      // only the palette and the modal around it stays up.
      e.preventDefault();
      closePanel();
      return;
    }

    const nextIndex = {
      ArrowRight: Math.min(activeIndex + 1, lastIndex),
      ArrowDown: Math.min(activeIndex + 1, lastIndex),
      ArrowLeft: Math.max(activeIndex - 1, 0),
      ArrowUp: Math.max(activeIndex - 1, 0),
      Home: 0,
      End: lastIndex
    }[e.key];

    if (nextIndex === undefined) return;
    e.preventDefault();
    setActiveIndex(nextIndex);
  };

  const fieldCls = [styles.colorField, className].filter(Boolean).join(' ');

  return (
    <div ref={containerRef} className={styles.swatchDropdown}>
      <InputField
        label={label}
        onClick={() => (isOpen ? setIsOpen(false) : handleOpen())}
        active={isOpen}
        className={fieldCls}
        ariaExpanded={isOpen}
        triggerRef={triggerRef}
      >
        <span
          className={styles.triggerDot}
          style={{ backgroundColor: selected?.color }}
          title={selected?.label}
        />
        <span className={styles.srOnly}>{selected?.label}</span>
        <ChevronDown
          width={16}
          height={16}
          className={isOpen ? styles.chevronOpen : styles.chevron}
        />
      </InputField>

      {isOpen && (
        <div className={styles.swatchPanel} {...escapeLayerProps}>
          {options.map((option, index) => (
            <button
              key={option.id}
              ref={(el) => {
                swatchRefs.current[index] = el;
              }}
              type="button"
              aria-pressed={option.id === selectedId}
              onKeyDown={handleSwatchKeyDown}
              // Only the active swatch is tabbable, so Tab leaves the palette
              // rather than stepping through every colour.
              tabIndex={index === activeIndex ? 0 : -1}
              className={option.id === selectedId ? styles.swatchActive : styles.swatch}
              style={{ backgroundColor: option.color }}
              onClick={() => handleSelect(option.id)}
              aria-label={option.label}
              title={option.label}
            />
          ))}
        </div>
      )}
    </div>
  );
};
